// filepath: src/utils/animationUtils.ts
import type { EmotionType } from "@src/sentiment";

/**
 * Available animation types for the chatbot
 *
 * Maps to Lottie animation files in assets/lottie_files/
 * - "happy": Positive/joyful animations
 * - "sad": Negative/melancholic animations
 * - "neutral": Default/thinking animations
 */
export type AnimationType = "happy" | "sad" | "neutral";

/**
 * Maps emotion types to animation types for chatbot display
 *
 * This utility function provides a centralized mapping between the 7 emotion types
 * detected by sentiment analysis and the 3 available animation types.
 *
 * **Mapping Logic:**
 * - `joy`, `surprise` → "happy" (positive emotions)
 * - `sadness`, `fear`, `disgust`, `anger` → "sad" (negative emotions)
 * - `neutral` → "neutral" (default state)
 *
 * **Future Enhancements:**
 * Could add more granular animations like:
 * - "angry" for anger emotion
 * - "scared" for fear emotion
 * - "surprised" for surprise emotion
 *
 * @param dominantEmotion - The dominant emotion type detected from text analysis
 * @returns The corresponding animation type to display
 *
 * @example
 * ```typescript
 * const animation = getAnimationType("joy");     // Returns "happy"
 * const animation = getAnimationType("anger");   // Returns "sad"
 * const animation = getAnimationType("neutral"); // Returns "neutral"
 * ```
 *
 * @see {@link EmotionType} for all available emotion types
 * @see {@link AnimationType} for all available animation types
 */
export function getAnimationType(dominantEmotion: EmotionType): AnimationType {
  switch (dominantEmotion) {
    case "joy":
    case "surprise":
      return "happy";
    case "sadness":
    case "fear":
    case "disgust":
      return "sad";
    case "anger":
      return "sad"; // Could add an "angry" animation in the future
    case "neutral":
    default:
      return "neutral";
  }
}
